import { Subscription } from 'rxjs';
import { chunk } from 'lodash';
import { Injector } from '@angular/core';
import { auditTime } from 'rxjs/operators';
import { BoardItemsRepository } from 'src/app/state/board-items-repository.interface';
import { isScreenShareAnnotationsSpaceID } from 'src/app/sessions/common/session-call-user/screen-share-annotations/screen-share-annotations.utils';
import { ensureAllObjectAreLive, YCanvas, YStaticCanvas } from '../../wb-canvas/fabric-utils';
import { BoardItemsStackingOrderManager } from '../board-items-stacking-order-manager';
import { YSessionBoardItemsAdapter } from '../../../common/y-session-board-items-adapter.service';
import { BoardItemsInteractionsManager } from '../board-items-interactions-manager';
import { BoardItemsEncoderDecoder } from '../../../../services/board-items-encoder-decoder.service';
import { BoardItem } from './board.item';
import { FabricExtraProperties, FabricObject } from './board-items-manager';
import {
  animateCanvasDrawOn,
  drawOnStaggerDelay,
  openAgentLiveGate,
  shouldAnimateAgentEntrance,
} from './agent-drawon';
import {
  denormalizeObjectCoordinates,
  isValidCanvasDimensions,
} from './board-items-normalized-coordinates.utils';

/**
 * Service responsible for managing incoming updates to board items from remote sources.
 * Handles synchronization of remote changes to the local canvas, including:
 * - Processing remote updates in batches (max 20,000 objects)
 * - Managing object additions, removals, and modifications
 * - Coordinating with stacking order and interaction managers
 * - Handling canvas state updates and cleanup
 *
 * Key responsibilities:
 * - Diffing local and remote objects to determine changes
 * - Applying remote updates to local canvas
 * - Managing selection state during updates
 * - Providing cleanup and destruction methods
 */
export class BoardItemsInBoundUpdatesManagerService {
  OBJECTS_BATCH_MAX_SIZE = 20000;

  private canvas: YCanvas | YStaticCanvas;
  private spaceId: string;
  private boardId: string;

  private currentBoardSubscription = new Subscription();
  private remoteUpdateToCanvasSubscription = new Subscription();
  private selectionCreatedSubscription = new Subscription();
  private selectionUpdatedSubscription = new Subscription();
  private objectModifiedSubscription = new Subscription();
  private objectAddedSubscription = new Subscription();
  private objectRemovedSubscription = new Subscription();

  private ySessionBoardItemsAdapter: YSessionBoardItemsAdapter;
  private boardItemsStackingOrderManager: BoardItemsStackingOrderManager;
  private boardItemsInteractionsManager: BoardItemsInteractionsManager;
  private boardItemsEncoderDecoder: BoardItemsEncoderDecoder;

  constructor(
    canvas: YCanvas | YStaticCanvas,
    spaceId: string,
    boardId: string,
    injector: Injector,
    private afterUpdatesCommitted = () => {},
  ) {
    this.ySessionBoardItemsAdapter =
      injector.get<YSessionBoardItemsAdapter>(YSessionBoardItemsAdapter);
    this.boardItemsStackingOrderManager = injector.get<BoardItemsStackingOrderManager>(
      BoardItemsStackingOrderManager,
    );
    this.boardItemsInteractionsManager = injector.get<BoardItemsInteractionsManager>(
      BoardItemsInteractionsManager,
    );

    this.boardItemsEncoderDecoder =
      injector.get<BoardItemsEncoderDecoder>(BoardItemsEncoderDecoder);

    this.canvas = canvas;
    this.spaceId = spaceId;
    this.boardId = boardId;
  }

  public onBoardChange(selectedBoardUid: string, boardItemsRepository?: BoardItemsRepository) {
    this.clearCanvas();
    this.boardId = selectedBoardUid;
    this.canvas.boardId = selectedBoardUid;
    // (Re)arm the agent "draw-on" entrance so this board's initial backfill —
    // which lands immediately below — is NOT animated, only live agent writes.
    // Draw-on is a LIVE-board affordance only: board-switcher thumbnails render on a
    // YStaticCanvas and must never animate, and a preview must not (re)arm the shared
    // global gate — doing so would suppress the MAIN board's draw-on whenever the
    // boards panel opens. So only the live canvas arms it.
    if (!this.canvas.isStatic) {
      openAgentLiveGate();
    }

    this.remoteUpdateToCanvasSubscription.unsubscribe();
    this.remoteUpdateToCanvasSubscription = this.ySessionBoardItemsAdapter
      .boardItems$(this.spaceId, this.boardId, boardItemsRepository)
      // Audit time was added because when we modify a group of objects, we still process old canvas items and fabric objects separately,
      //  resulting in having two emissions for that group update, one is when canvas items updated and one when fabric objects are updated.
      //  this can result in minor issues such as deselecting the updated group locally,
      //  Deselecting the group stems from:
      //    the first update can have the old version of an object (canvas object) and a new version of another object (fabric object)
      //    While the local state have both in the new state
      //    Making the code consider the object that is emitted and having the old update still to be an update
      .pipe(auditTime(0))
      .subscribe(({ objects }) => this.commitRemoteUpdatesToLocalCanvas(objects));
  }

  private commitRemoteUpdatesToLocalCanvas(objects: FabricObject[]) {
    const localObjects = this.canvas.getObjects() as FabricObject[];
    const remoteObjects = objects.filter(Boolean);

    const objectsDifferences = this.getObjectsDifferences(localObjects, remoteObjects);

    const objectsRemoved = objectsDifferences.objectsRemoved;
    const objectsModified = this.boardItemsEncoderDecoder.decodeFabricItems(
      objectsDifferences.objectsModified,
    );
    const objectsAdded = this.boardItemsEncoderDecoder.decodeFabricItems(
      objectsDifferences.objectsAdded,
    );
    this.denormalizeObjectCoordinatesIfNeeded(objectsModified);
    this.denormalizeObjectCoordinatesIfNeeded(objectsAdded);

    this.triggerDeselectionIfNecessary(objectsModified, objectsRemoved);

    // please note the order of the called functions in bellow is important, update -> remove -> add objects
    this.canvas.stateManager.updateObjectsInLocalCanvas(objectsModified);
    this.removeObjectsFromLocalCanvas(objectsRemoved);
    this.addObjectsToLocalCanvas(objectsAdded);
    this.boardItemsStackingOrderManager.stackObjects(remoteObjects, objectsModified, this.canvas);

    this.canvas.requestRenderAll();
    this.afterUpdatesCommitted();
  }

  private addObjectsToLocalCanvas(objectsToAdd: FabricObject[]) {
    if (objectsToAdd.length === 0) {
      return;
    }
    const objectsBatches = chunk(objectsToAdd, this.OBJECTS_BATCH_MAX_SIZE);

    for (const objectsBatch of objectsBatches) {
      objectsBatch.forEach((object) => (object.skipRemoteAddition = true)); // to not trigger object:added event again

      ensureAllObjectAreLive(objectsBatch, (liveObjects) => {
        this.canvas.add(...liveObjects);
      });
    }

    this.canvas.fire('remote:added', { objectsAdded: objectsToAdd });

    // Never play the draw-on entrance on a static preview (board-switcher thumbnail):
    // the objects are already added + rendered above; only the LIVE board animates.
    if (this.canvas.isStatic) {
      return;
    }

    // "Draw-on" entrance for the AI tutor's live writes. Canvas objects (text,
    // scribbles, shapes) animate here via a growing clipPath; HTML-backed items
    // (KaTeX formulas, AngularComponentClass set) animate via CSS in
    // board.item.ts onHtmlAdded, so skip them here to avoid a double effect.
    const liveObjects = this.canvas.getObjects() as (FabricObject & { uid?: string })[];

    // Resolve the live fabric instances to animate. ensureAllObjectAreLive enlivens
    // plain fabric objects into NEW instances, so the object actually on the canvas
    // may not be `object`; resolve by uid (falls back to `object` for already-live
    // board items).
    const toAnimate: FabricObject[] = [];
    for (const object of objectsToAdd) {
      const item = object as FabricObject & {
        uid?: string;
        userId?: string;
        AngularComponentClass?: unknown;
      };
      if (item.AngularComponentClass) {
        continue;
      }
      if (!shouldAnimateAgentEntrance(item.userId)) {
        continue;
      }
      const live = liveObjects.find((o) => o.uid && o.uid === item.uid) ?? object;
      toAnimate.push(live);
    }

    // When several agent objects arrive in one batch, stagger their reveals so the
    // student can follow them one-by-one instead of everything animating at once.
    // Order top-to-bottom then left-to-right (reading order) for a readable reveal;
    // a single object is index 0 / no delay, so its behaviour is unchanged.
    toAnimate.sort((a, b) => {
      const ay = (a.top as number) ?? 0;
      const by = (b.top as number) ?? 0;
      if (ay !== by) {
        return ay - by;
      }
      return ((a.left as number) ?? 0) - ((b.left as number) ?? 0);
    });
    toAnimate.forEach((live, index) => {
      animateCanvasDrawOn(this.canvas, live, drawOnStaggerDelay(index, toAnimate.length));
    });
  }

  private removeObjectsFromLocalCanvas(objectsToRemove: FabricObject[]) {
    if (objectsToRemove.length === 0) {
      return;
    }

    objectsToRemove
      .filter((obj) => !obj.excludeFromExport) // to not remove the related text of sticky note
      .forEach((object) => {
        object.skipRemoteDeletion = true; // to not trigger object:removed event again
      });

    this.canvas.remove(...objectsToRemove);
  }

  private getObjectsDifferences(
    localObjects: FabricObject[],
    remoteObjects: FabricObject[],
  ): {
    objectsRemoved: FabricObject[];
    objectsModified: FabricObject[];
    objectsAdded: FabricObject[];
  } {
    const localObjectsIdToVersionIdAndIndexMap = new Map<
      string,
      { versionedId: string; index: number }
    >();
    localObjects.forEach((obj, index) => {
      const id = obj.uid;
      const versionedId = obj.vuid;
      if (!id || !versionedId) {
        return;
      }
      localObjectsIdToVersionIdAndIndexMap.set(id, { versionedId, index });
    });

    const objectsModified: FabricObject[] = [];
    const objectsAdded: FabricObject[] = [];
    const objectsInLocalAndRemote = new Set<string>();

    remoteObjects.forEach((remoteObj) => {
      const { uid, vuid: removeVersionedId } = remoteObj;
      if (!uid || !removeVersionedId) {
        return;
      }

      const localVersionIdAndIdx = localObjectsIdToVersionIdAndIndexMap.get(uid);

      // both in local and remote => either modified or unchanged
      if (localVersionIdAndIdx) {
        objectsInLocalAndRemote.add(uid);
        // objects are not of the same version id => modified
        if (localVersionIdAndIdx.versionedId !== removeVersionedId) {
          objectsModified.push(remoteObj);
        }
      } else {
        // not in local but in remote => added
        objectsAdded.push(remoteObj);
      }
    });

    const objectsRemoved: FabricObject[] = [];
    localObjectsIdToVersionIdAndIndexMap.forEach(({ index }, uid) => {
      // Objects not still exists => removed ones
      if (!objectsInLocalAndRemote.has(uid)) {
        objectsRemoved.push(localObjects[index]);
      }
    });

    return {
      objectsModified,
      objectsRemoved,
      objectsAdded,
    };
  }

  // This is needed because the selection in fabric can be broken if:
  //  We deleted an item from it,
  //  We changed an element position from it, because the position won't be applied correctly as there would be a transform applied to it from the selection
  // This can be further improved by re-selecting the objects after the update was done
  private triggerDeselectionIfNecessary(
    objectsModified: ((Object & FabricExtraProperties) | BoardItem)[],
    objectsRemoved: FabricObject[],
  ) {
    if (!this.isDynamicCanvas(this.canvas)) {
      return;
    }

    let modifiedAndDeletedObjectsIds = [
      ...objectsModified.map((o) => o.uid),
      ...objectsRemoved.map((o) => o.uid),
    ];
    let selectedObjectsIds = this.canvas.getActiveObjects().map((o) => {
      if ((o as any).parentBoardItem) {
        return (o as any).parentBoardItem.uid;
      } else {
        return (o as FabricObject).uid;
      }
    });

    if (modifiedAndDeletedObjectsIds.length < selectedObjectsIds.length) {
      // creating a set of the short array is more optimal
      [selectedObjectsIds, modifiedAndDeletedObjectsIds] = [
        modifiedAndDeletedObjectsIds,
        selectedObjectsIds,
      ];
    }

    const set = new Set(selectedObjectsIds);
    const modifiedObjectWasSelected = modifiedAndDeletedObjectsIds.some((item) => set.has(item));

    if (modifiedObjectWasSelected) {
      this.boardItemsInteractionsManager.triggerDeselectAll(this.canvas);
    }
  }

  clearCanvas() {
    const objectsOnCanvas = this.canvas.getObjects();
    this.canvas.clear();
    for (const object of objectsOnCanvas) {
      // Because this event is needed by the objects so they can know that they have been removed
      object.fire('removed');
    }
  }

  public destroyService() {
    this.currentBoardSubscription.unsubscribe();
    this.remoteUpdateToCanvasSubscription.unsubscribe();
    this.selectionCreatedSubscription.unsubscribe();
    this.selectionUpdatedSubscription.unsubscribe();
    this.objectModifiedSubscription.unsubscribe();
    this.objectAddedSubscription.unsubscribe();
    this.objectRemovedSubscription.unsubscribe();
  }

  private isDynamicCanvas(canvas: YCanvas | YStaticCanvas): canvas is YCanvas {
    return !canvas.isStatic;
  }

  private denormalizeObjectCoordinatesIfNeeded(objects: FabricObject[]): void {
    if (!isScreenShareAnnotationsSpaceID(this.spaceId)) {
      return;
    }

    const dimensions = {
      width: this.canvas.getWidth(),
      height: this.canvas.getHeight(),
    };
    if (!isValidCanvasDimensions(dimensions)) {
      return;
    }

    for (const object of objects) {
      denormalizeObjectCoordinates(object, dimensions);
    }
  }
}
